import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { Glue42Core } from '@glue42/core';
import BBGMarketData, { ConnectionStatus } from '@glue42/bbg-market-data';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { BBGMarketDataAPI } from '@glue42/bbg-market-data/dist/cjs/bbg-market-data-api';
import { Disposable } from 'using-statement';
import {
    BloombergMarketDataRequest,
    isBarRequest,
    isTickRequest,
    MarketDataRequestBarRequest,
    requestUid,
} from 'marketData/MarketDataRequest';
import { DapiRequestManagerBase } from './requests/DapiRequestManagerBase';
import { DapiTickDataRequestManager } from 'app/glue/bloomberg/requests/DapiTickDataRequestManager';
import { DapiBarDataRequestManager } from './requests/DapiBarDataRequestManager';
import {
    addBarMessage,
    addMessage,
    BarData,
    clearMessages,
    setExternalMarketDataSource,
    TickData,
} from 'marketData/store/marketDataSlice';
import {
    BarDataReceivedHandler,
    RefDataReceivedHandler,
    RefDataReceivedHandlerOptions,
    TickDataReceivedHandler,
} from 'marketData/ExternalMarketDataStrategy';
import {
    ActionsIntervalDispatcher,
    ActionsIntervalDispatcherFactory,
} from 'store/ActionsDispatcher';
import { showInfoAsync, showWarningAsync } from 'notification/store/thunks';
import { AnyAction, AnyThunkAction } from 'store/store';

interface Options {
    actionsIntervalDispatcherFactory: ActionsIntervalDispatcherFactory;
    embeddedApiService: EmbeddedApiService;
    loggingService: LoggingService;
    glueInteropApi: Glue42Core.Interop.API;
}

const NoConnectionMessage = 'No Bloomberg connection for market data';
const LostConnectionMessage = 'Bloomberg connection lost';

export class GlueBloombergBridge implements Disposable {
    private logger: Logger;
    private readonly loggingService: LoggingService;
    private readonly mktDataApi: BBGMarketDataAPI;
    public readonly actionsIntervalDispatcherFactory: ActionsIntervalDispatcherFactory;
    public readonly actionsIntervalDispatcher: ActionsIntervalDispatcher;
    private readonly glueInteropApi: Glue42Core.Interop.API;
    private readonly unsubscribeConnectionStatusChanged: () => void;

    private connectionStatus: ConnectionStatus = ConnectionStatus.Disconnected;
    private requests: DapiRequestManagerBase[] = [];
    private barDataReceivedHandler: undefined | BarDataReceivedHandler =
        undefined;
    private tickDataReceivedHandler: undefined | TickDataReceivedHandler =
        undefined;
    private refDataReceivedHandler: undefined | RefDataReceivedHandler =
        undefined;

    private disposed = false;

    public constructor(options: Options) {
        this.loggingService = options.loggingService;
        this.logger = buildLogger(
            'GlueBloombergBridge',
            options.loggingService
        );
        this.actionsIntervalDispatcherFactory =
            options.actionsIntervalDispatcherFactory;
        this.actionsIntervalDispatcher =
            options.actionsIntervalDispatcherFactory.new();

        this.glueInteropApi = options.glueInteropApi;

        this.mktDataApi = BBGMarketData(this.glueInteropApi, {
            debug: true,
        });
        this.unsubscribeConnectionStatusChanged =
            this.mktDataApi.onConnectionStatusChanged(
                this.onConnectionStatusChanged.bind(this)
            );

        (async () => await this.checkLoggedIn())();
    }

    public subscribe(request: BloombergMarketDataRequest) {
        const options = {
            mktDataApi: this.mktDataApi,
            loggingService: this.loggingService,
            glueInteropApi: this.glueInteropApi,
            dispatcher: this.actionsIntervalDispatcher,
        };

        if (this.connectionStatus === ConnectionStatus.Disconnected) {
            (async () => await this.reconnect())();
        }

        if (this.disposed) {
            return;
        }
        if (!request) {
            return;
        }

        const uid = requestUid(request);

        if (isTickRequest(request)) {
            let bloombergTickRequest = this.requests.find(
                (r) => requestUid(r.request) === uid
            );
            const tickOptions = {
                ...options,
                onTickDataReceived: this.handleTickDataReceived.bind(this),
            };

            if (!bloombergTickRequest) {
                bloombergTickRequest = new DapiTickDataRequestManager(
                    request,
                    tickOptions
                );

                this.requests.push(bloombergTickRequest);
            }
            if (this.connectionStatus === ConnectionStatus.Connected) {
                bloombergTickRequest.subscribe();
            } else {
                this.logger.debug(
                    'Will subscribe for market data once Bloomberg connection established',
                    request
                );
            }
        }

        const barOptions = {
            ...options,
            onBarDataReceived: this.handleBarDataReceived.bind(this),
            onRefDataReceived: this.handleRefDataReceived.bind(this),
            onBarDataMessage: this.handleBarDataMessage.bind(this),
        };

        if (isBarRequest(request)) {
            let bloombergRequest = this.requests.find(
                (r) => requestUid(r.request) === uid
            );
            if (!bloombergRequest) {
                bloombergRequest = new DapiBarDataRequestManager(
                    request,
                    barOptions
                );
                this.requests.push(bloombergRequest);
            }

            if (this.connectionStatus === ConnectionStatus.Connected) {
                bloombergRequest.subscribe();
            } else {
                this.logger.debug(
                    'Will subscribe for bar data once Bloomberg connection established',
                    request
                );
            }
        }
    }

    public unsubscribe(request: BloombergMarketDataRequest) {
        const existing = this.findRequest(request);
        if (existing) {
            (async () => existing.unsubscribe())();
        }
    }

    public subscribeMarketBarDataReceived(handler: BarDataReceivedHandler) {
        this.barDataReceivedHandler = handler;
    }

    public subscribeMarketTickDataReceived(handler: TickDataReceivedHandler) {
        this.tickDataReceivedHandler = handler;
    }

    public subscribeRefDataReceived(handler: RefDataReceivedHandler) {
        this.refDataReceivedHandler = handler;
    }

    private findRequest(request: BloombergMarketDataRequest | undefined) {
        if (!request) {
            return undefined;
        }
        return this.requests.find(
            (r) => requestUid(r.request) === requestUid(request)
        );
    }

    public dispose() {
        this.logger.debug('Disposing');
        for (const request of this.requests) {
            request.dispose();
        }
        this.unsubscribeConnectionStatusChanged();
        this.disposed = true;
    }

    private onConnectionStatusChanged(connectionStatus: ConnectionStatus) {
        this.logger.info('Connection status changed to ' + connectionStatus);

        if (
            connectionStatus === ConnectionStatus.Disconnected &&
            this.connectionStatus === ConnectionStatus.Connected
        ) {
            // Lost connection
            this.dispatchImmediate(
                showWarningAsync('Bloomberg Connection Lost') as AnyThunkAction
            );
            this.dispatchImmediate(addMessage(LostConnectionMessage));
        }

        this.connectionStatus = connectionStatus;

        if (connectionStatus === ConnectionStatus.Connected) {
            this.dispatchImmediate(
                showInfoAsync(
                    'Bloomberg Connection Established'
                ) as AnyThunkAction
            );

            this.onConnected();
        } else {
            this.checkBridgeInteropMethodRegistered();
        }
    }

    private handleBarDataReceived(data: BarData[]) {
        if (!this.barDataReceivedHandler) {
            this.logger.critical('No bar data handler received');
            return;
        }
        this.barDataReceivedHandler(data);
    }

    private handleTickDataReceived(data: TickData[]) {
        if (!this.tickDataReceivedHandler) {
            this.logger.critical('No tick data handler received');
            return;
        }
        this.tickDataReceivedHandler(data);
    }

    private handleBarDataMessage(
        request: MarketDataRequestBarRequest,
        message: string
    ) {
        this.dispatchImmediate(
            addBarMessage({
                symbol: request.symbol,
                binSize: request.barSize,
                message,
            })
        );
    }

    private handleRefDataReceived(options: RefDataReceivedHandlerOptions) {
        if (!this.refDataReceivedHandler) {
            this.logger.critical('No ref data received handler received');
            return;
        }
        this.refDataReceivedHandler(options);
    }

    private dispatchImmediate(
        action: { payload: unknown; type: string } | AnyThunkAction
    ) {
        const dispatcher = this.actionsIntervalDispatcherFactory.new();
        dispatcher.dispatch(action as unknown as AnyAction);
        dispatcher.dispose();
    }

    private checkBridgeInteropMethodRegistered() {
        const methods = this.glueInteropApi.methods({
            name: 'T42.MDFApi.CreateSubscriptionRequest',
        });
        if (methods.length === 0) {
            this.dispatchImmediate(
                showWarningAsync(
                    'Glue42 Bloomberg bridge is not running'
                ) as unknown as AnyThunkAction
            );
            this.dispatchImmediate(addMessage(NoConnectionMessage));
        }
    }

    private async checkLoggedIn() {
        try {
            const result =
                await this.glueInteropApi.invoke('T42.BBG.IsLoggedIn');

            const loggedIn = result.returned.Result;

            if (loggedIn) {
                this.logger.debug('Logged into Bloomberg');
                return true;
            }

            this.dispatchImmediate(
                showWarningAsync(
                    'No Bloomberg Connection'
                ) as unknown as AnyThunkAction
            );

            this.dispatchImmediate(addMessage(NoConnectionMessage));
        } catch (err) {
            this.logger.warn('Error checking Bloomberg login status', err);
        }
    }

    private async reconnect() {
        try {
            const result =
                await this.glueInteropApi.invoke('T42.BBG.Reconnect');
            const hasReconnected = result.returned.reconnected;

            if (hasReconnected) {
                this.logger.debug('Reconnected');
                this.onConnected();
            } else {
                this.logger.debug('Could not reconnect');
            }
        } catch (err) {
            this.logger.warn('Error checking Bloomberg login status', err);
        }
    }

    private onConnected() {
        this.connectionStatus = ConnectionStatus.Connected;

        this.dispatchImmediate(
            setExternalMarketDataSource('Glue42 Bloomberg Bridge')
        );
        this.dispatchImmediate(clearMessages());

        for (const request of this.requests) {
            if (request.status !== 'Running') {
                request.subscribe();
            }
        }
    }
}
